
import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

const API_BASE =
  (typeof import.meta !== 'undefined'
    ? import.meta.env?.VITE_API_BASE
    : process.env?.REACT_APP_API_BASE) || '/.netlify/functions/api';

const API_ROOT = String(API_BASE || '').replace(/\/$/, '');

function getCsrfToken() {
  const m = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/);
  return m ? decodeURIComponent(m[1]) : '';
}

function csrfHeaders() {
  const token = getCsrfToken();
  return token ? { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': token } : { 'Content-Type': 'application/json' };
}

const NOTIFICATIONS_URL = `${API_ROOT}/notifications`;
const MARK_ALL_READ_URL = `${API_ROOT}/notifications/mark-all-read`;
const makeMarkOneReadUrl = (id) => `${API_ROOT}/notifications/${id}/read`;


const GRADIENT_BG =
  'linear-gradient(0deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(40, 227, 218, 0.2) 0%, rgba(40, 227, 218, 0) 100%), #081423';


const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  return (
    /iP(hone|od|ad)/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
};

const NotificationsPanel = ({ isOpen, onClose, variant = 'panel', onUnreadCountChange }) => {
  const { isAuthenticated } = useAuth();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);

  const IOS = useMemo(() => isIOS(), []);


  const fetchNotifications = async () => {
    if (!isAuthenticated) {
      setNotifications([]);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(NOTIFICATIONS_URL, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!res.ok) {
        setNotifications([]);
        return;
      }

      const data = await res.json();
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data.notifications)
        ? data.notifications
        : [];

      setNotifications(
        list.map((n, idx) => ({
          id: n.id ?? n.notificationId ?? n._id ?? `notif-${idx}`,
          title: n.title || 'إشعار',
          body: n.body || n.message || '',
          createdAt: n.createdAt || n.date || n.created_at || null,
          isRead: Boolean(n.isRead ?? n.read ?? false),
          type: n.type || 'general'
        }))
      );
    } catch (err) {
      console.error('Error fetching notifications in panel', err);
    } finally {
      setLoading(false);
    }
  };

  const doMarkAllRead = async () => {
    if (!notifications.some((n) => !n.isRead)) return;

    setMarkingAll(true);
    try {
      const res = await fetch(MARK_ALL_READ_URL, {
        method: 'POST',
        credentials: 'include',
        headers: csrfHeaders()
      });

      if (!res.ok) return;

      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    } catch (err) {
      console.error('Error marking all read in panel', err);
    } finally {
      setMarkingAll(false);
    }
  };

  const handleMarkOneRead = async (id) => {
    const target = notifications.find((n) => n.id === id);
    if (!target || target.isRead) return;

    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
    );

    try {
      const res = await fetch(makeMarkOneReadUrl(id), {
        method: 'POST',
        credentials: 'include',
        headers: csrfHeaders()
      });

      if (!res.ok) {

        setNotifications((prev) =>
          prev.map((n) => (n.id === id ? { ...n, isRead: false } : n))
        );
      }
    } catch (err) {
      console.error('Error mark one read in panel', err);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, isRead: false } : n))
      );
    }
  };


  useEffect(() => {
    if (!isOpen) return;
    if (!isAuthenticated) {
      setNotifications([]);
      return;
    }
    fetchNotifications();

  }, [isOpen, isAuthenticated]);

  const unreadCount = notifications.filter((n) => !n.isRead).length;


  useEffect(() => {
    onUnreadCountChange?.(unreadCount);
  }, [unreadCount, onUnreadCountChange]);

  const formatDate = (value) => {
    if (!value) return '';
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toLocaleString('ar-SA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return String(value);
    }
  };

  if (!isOpen) return null;


  const renderContent = (inlineClose = true) => (
    <>
      {            }
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{
              background:
                'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(17, 101, 97, 0.25)'
            }}
          >
            <span className="text-sm">🔔</span>
          </div>
          <div className="flex flex-col text-right">
            <span
              className="text-sm font-semibold text-white"
              style={{ fontFamily: 'Alexandria' }}
            >
              الإشعارات
            </span>
            <span
              className="text-[11px] text-white/70"
              style={{ fontFamily: 'Alexandria' }}
            >
              غير مقروءة: {unreadCount}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {notifications.length > 0 && (
            <button
              onClick={doMarkAllRead}
              disabled={markingAll || unreadCount === 0}
              className="text-[11px] px-3 py-1 rounded-full disabled:opacity-60"
              style={{
                background:
                  unreadCount === 0
                    ? 'rgba(255,255,255,0.05)'
                    : 'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)',
                border: '1px solid rgba(40, 227, 218, 0.35)',
                color: '#fff',
                fontFamily: 'Alexandria'
              }}
            >
              {markingAll
                ? 'جاري التعليم…'
                : unreadCount === 0
                ? 'لا يوجد غير مقروء'
                : 'تعليم الكل كمقروء'}
            </button>
          )}

          {inlineClose && (
            <button
              onClick={onClose}
              className="w-7 h-7 rounded-full flex items-center justify-center text-sm text-white border border-white/20 hover:bg-white/10"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {             }
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-3 scrollbar-thin">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <div className="w-8 h-8 border-2 border-[#28E3DA80] border-t-transparent rounded-full animate-spin" />
            <span
              className="text-xs text-gray-300"
              style={{ fontFamily: 'Alexandria' }}
            >
              جاري تحميل الإشعارات...
            </span>
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-center">
            <p
              className="text-sm text-gray-300 max-w-[260px]"
              style={{ fontFamily: 'Alexandria' }}
            >
              لما يصير فيه شيء جديد يهمك، يطلع لك إشعار هنا.
            </p>
          </div>
        ) : (
          notifications.map((n) => (
            <div
              key={n.id}
              onClick={() => handleMarkOneRead(n.id)}
              className="rounded-2xl px-3 py-2 cursor-pointer transition-all"
              style={{
                background: n.isRead
                  ? 'rgba(0,0,0,0.35)'
                  : 'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.25) 0%, rgba(40, 227, 218, 0) 100%), #0b1c2b',
                border: n.isRead
                  ? '1px solid rgba(40, 227, 218, 0.2)'
                  : '1px solid rgba(40, 227, 218, 0.7)'
              }}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-2">
                  {!n.isRead && (
                    <span className="inline-block w-2 h-2 rounded-full bg-[#28E3DA]" />
                  )}
                  <span
                    className="text-[11px] px-2 py-0.5 rounded-full text-white/90"
                    style={{
                      background: 'rgba(255,255,255,.08)',
                      border: '1px solid rgba(255,255,255,.15)',
                      fontFamily: 'Alexandria'
                    }}
                  >
                    {n.type === 'system'
                      ? 'نظام'
                      : n.type === 'security'
                      ? 'أمان'
                      : n.type === 'social'
                      ? 'تفاعل'
                      : n.type === 'planner'
                      ? 'مهام'
                      : 'عام'}
                  </span>
                </div>
                {n.createdAt && (
                  <span
                    className="text-[10px] text-white/70"
                    style={{ fontFamily: 'Alexandria' }}
                  >
                    {formatDate(n.createdAt)}
                  </span>
                )}
              </div>

              <div className="space-y-1" style={{ direction: 'rtl' }}>
                <div
                  className="text-[13px] text-white font-medium"
                  style={{ fontFamily: 'Alexandria' }}
                >
                  {n.title}
                </div>
                {n.body && (
                  <div
                    className="text-[12px] text-white/90 leading-5 line-clamp-3"
                    style={{ fontFamily: 'Alexandria' }}
                  >
                    {n.body}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );


  if (variant === 'modal') {
    const handleBackdropClick = (e) => {
      if (e.target === e.currentTarget && onClose) {
        onClose();
      }
    };

    return (
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center"
        dir="rtl"
        onClick={handleBackdropClick}
        style={{
          height: IOS ? '100svh' : '100dvh',
          position: 'fixed',
          overflow: 'hidden'
        }}
      >
        <div
          className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm cursor-pointer"
          style={{
            backdropFilter: IOS ? 'none' : 'blur(4px)',
            WebkitBackdropFilter: IOS ? 'none' : 'blur(4px)'
          }}
        ></div>

        <div
          className="relative flex flex-col justify-start items-center rounded-2xl z-10"
          style={{
            width: '92%',
            maxWidth: '24rem',

            height: '420px',
            maxHeight: IOS ? '82svh' : '90vh',
            padding: '24px',
            gap: '18px',
            background: GRADIENT_BG,
            borderRadius: '16px',
            border: '1px solid rgba(40, 227, 218, 0.25)',
            boxShadow: '0px 8px 32px 0px rgba(255, 255, 255, 0.08)',
            backdropFilter: IOS ? 'none' : 'blur(2px)',
            WebkitBackdropFilter: IOS ? 'none' : 'blur(2px)',
            overflow: 'hidden',
            paddingBottom: 'env(safe-area-inset-bottom)'
          }}
        >
          {                                                            }
          <button
            type="button"
            onClick={onClose}
            aria-label="إغلاق"
            className="absolute top-3 left-3 h-8 w-8 rounded-full flex items-center justify-center border text-white"
            style={{ borderColor: 'rgba(40, 227, 218, 0.25)' }}
          >
            <span className="text-lg leading-none">×</span>
          </button>

          {                                         }
          <div className="w-full h-full flex flex-col rounded-xl overflow-hidden">
            {renderContent(false)}
          </div>
        </div>
      </div>
    );
  }


  return (
    <div
      className="fixed z-[1200]"
      dir="rtl"
      style={{
        top: '80px',
        left: '80px',
        width: '360px',
        height: '520px',
        pointerEvents: 'auto'
      }}
    >
      <div
        className="relative flex flex-col h-full rounded-3xl overflow-hidden"
        style={{
          background:
            'linear-gradient(0deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(40, 227, 218, 0.2) 0%, rgba(40, 227, 218, 0) 100%), #050B16',
          border: '1px solid rgba(40, 227, 218, 0.35)',
          boxShadow: '0px 18px 50px rgba(0, 0, 0, 0.65)'
        }}
      >
        {renderContent(true)}
      </div>
    </div>
  );
};

export default NotificationsPanel;
